import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';

/**
 * Panel for configuring and launching a new inventory session.
 * Shows setup options (full vs partial, category, product selection).
 */
const NewInventoryPanel = ({
  inventoryType, setInventoryType,
  categories,
  selectedCategory, setSelectedCategory,
  searchTerm, setSearchTerm,
  products,
  selectedProducts,
  productSearchTerm, setProductSearchTerm,
  productSearchResults: externalSearchResults,
  addToSelectedProducts,
  removeFromSelectedProducts,
  clearSelectedProducts,
  onStart, loading,
  locationId
}) => {
  // Wyszukiwarka częściowa — bezpośredni API call
  const [localSearch, setLocalSearch] = useState('');
  const [localResults, setLocalResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Wyszukiwarka całościowa — podgląd produktów
  const [fullSearch, setFullSearch] = useState('');
  const [fullResults, setFullResults] = useState([]);
  const [showFullDropdown, setShowFullDropdown] = useState(false);
  const fullSearchTimeoutRef = useRef(null);

  useEffect(() => {
    if (localSearch.trim().length < 2) {
      setLocalResults([]);
      setShowDropdown(false);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        let url = `/products/search?limit=15&query=${encodeURIComponent(localSearch.trim())}`;
        if (locationId) url += `&location_id=${locationId}`;
        const response = await api.get(url);
        const prods = response.data?.data?.products || response.data?.products || [];
        const existingIds = new Set(selectedProducts.map(p => p.id));
        const filtered = prods.filter(p => !existingIds.has(p.id));
        setLocalResults(filtered);
        setShowDropdown(filtered.length > 0);
      } catch (err) {
        console.error('Błąd wyszukiwania:', err);
      }
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [localSearch, locationId, selectedProducts]);

  // Wyszukiwarka całościowa — debounce 300ms
  useEffect(() => {
    if (fullSearch.trim().length < 2) {
      setFullResults([]);
      setShowFullDropdown(false);
      return;
    }
    if (fullSearchTimeoutRef.current) clearTimeout(fullSearchTimeoutRef.current);
    fullSearchTimeoutRef.current = setTimeout(async () => {
      try {
        let url = `/products/search?limit=50&query=${encodeURIComponent(fullSearch.trim())}`;
        if (locationId) url += `&location_id=${locationId}`;
        const response = await api.get(url);
        const prods = response.data?.data?.products || response.data?.products || [];
        setFullResults(prods);
        setShowFullDropdown(prods.length > 0);
      } catch (err) {
        console.error('Błąd wyszukiwania:', err);
      }
    }, 300);
    return () => { if (fullSearchTimeoutRef.current) clearTimeout(fullSearchTimeoutRef.current); };
  }, [fullSearch, locationId]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (product) => {
    addToSelectedProducts(product);
    setLocalSearch('');
    setLocalResults([]);
    setShowDropdown(false);
  };

  return (
  <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '4px' }}>
    <div style={{
      padding: '0.4rem 0.6rem', backgroundColor: 'var(--bg)',
      borderBottom: '1px solid var(--border)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    }}>
      <span style={{ fontWeight: '600', fontSize: '11px' }}>Ustawienia inwentaryzacji</span>
      <button
        style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--success)', color: 'var(--success)' , cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
        onClick={onStart}
        disabled={loading}
      >
        <i className="fas fa-play" style={{ fontSize: '10px' }}></i>
        Rozpocznij inwentaryzacje
      </button>
    </div>

    <div style={{ padding: '0.5rem' }}>
      {/* Type selection */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ fontSize: '10px', fontWeight: '600', display: 'block', marginBottom: '0.35rem' }}>
          <i className="fas fa-clipboard-list me-1" style={{ color: 'var(--purple)' }}></i>
          Typ inwentaryzacji
        </label>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {['full', 'partial'].map(type => (
            <label
              key={type}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                padding: '0.4rem 0.75rem',
                backgroundColor: inventoryType === type
                  ? (type === 'full' ? 'var(--info-light)' : 'var(--warning-light)')
                  : 'var(--bg)',
                border: inventoryType === type
                  ? `2px solid ${type === 'full' ? 'var(--info)' : 'var(--warning)'}`
                  : '1px solid var(--border)',
                borderRadius: '4px', cursor: 'pointer', fontSize: '11px'
              }}
            >
              <input
                type="radio"
                name="inventoryType"
                value={type}
                checked={inventoryType === type}
                onChange={() => setInventoryType(type)}
                style={{ margin: 0 }}
              />
              <i className={`fas ${type === 'full' ? 'fa-boxes' : 'fa-hand-pointer'}`} style={{ color: type === 'full' ? 'var(--info)' : 'var(--warning)' }}></i>
              <span><strong>{type === 'full' ? 'Calosciowa' : 'Czesciowa'}</strong> - {type === 'full' ? 'wszystkie produkty' : 'wybrane produkty'}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Full inventory — wyszukiwarka podglądu */}
      {inventoryType === 'full' && (
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', display: 'block', marginBottom: '0.15rem' }}>
                <i className="fas fa-filter me-1" style={{ color: 'var(--info)' }}></i>
                Kategoria (opcjonalnie)
              </label>
              <select
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Wszystkie kategorie</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
              <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Mozesz ograniczyc do wybranej kategorii</span>
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', display: 'block', marginBottom: '0.15rem' }}>
                <i className="fas fa-search me-1" style={{ color: 'var(--info)' }}></i>
                Szukaj produktow (podglad)
              </label>
              <input
                type="text"
                style={{
                  width: '100%', padding: '6px 10px', fontSize: '12px',
                  border: '2px solid var(--info)', borderRadius: '6px',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text)'
                }}
                placeholder="Wpisz nazwe, EAN lub kod (min. 2 znaki)..."
                value={fullSearch}
                onChange={(e) => setFullSearch(e.target.value)}
                onFocus={() => { if (fullResults.length > 0) setShowFullDropdown(true); }}
              />
              <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Podglad produktow do inwentaryzacji</span>
            </div>
          </div>

          {/* Wyniki wyszukiwania */}
          {showFullDropdown && fullResults.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <span style={{ fontWeight: '600', fontSize: '11px' }}>Znalezione produkty ({fullResults.length})</span>
              <div style={{ maxHeight: '250px', overflowY: 'auto', marginTop: '0.25rem', border: '1px solid var(--border)', borderRadius: '4px' }}>
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)' }}>
                    <tr>
                      {['LP', 'Nazwa', 'Kod / EAN', 'Stan systemowy'].map(h => (
                        <th key={h} style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: h === 'LP' ? 'center' : 'left', fontWeight: '600', color: 'var(--text-secondary)', width: h === 'LP' ? '35px' : 'auto' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fullResults.map((product, idx) => (
                      <tr key={product.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)' }}>{idx + 1}</td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <strong>{product.name || product.nazwa}</strong>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <code style={{ fontSize: '10px', backgroundColor: 'var(--border)', padding: '1px 4px', borderRadius: '2px' }}>{product.barcode || product.ean || product.kod_produktu || 'Brak'}</code>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: 'var(--cyan)', color: '#fff', borderRadius: '3px' }}>
                            {product.stock_quantity || 0} {product.unit || 'szt'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Partial inventory options */}
      {inventoryType === 'partial' && (
        <div style={{ marginTop: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', display: 'block', marginBottom: '0.15rem' }}>
                <i className="fas fa-filter me-1" style={{ color: 'var(--info)' }}></i>
                Filtruj po kategorii
              </label>
              <select
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Wszystkie kategorie</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>
            <div ref={dropdownRef} style={{ flex: '2', minWidth: '300px', position: 'relative' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', display: 'block', marginBottom: '0.15rem' }}>
                <i className="fas fa-plus-circle me-1" style={{ color: 'var(--success)' }}></i>
                Dodaj produkt do inwentaryzacji
              </label>
              <input
                type="text"
                style={{
                  width: '100%', padding: '6px 10px', fontSize: '12px',
                  border: '2px solid var(--success)', borderRadius: '6px',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text)'
                }}
                placeholder="Wpisz nazwe, kod kreskowy lub EAN (min. 2 znaki)..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                onFocus={() => { if (localResults.length > 0) setShowDropdown(true); }}
              />
              {showDropdown && localResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
                  borderRadius: '0 0 4px 4px', maxHeight: '250px',
                  overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  {localResults.map(product => (
                    <div
                      key={product.id}
                      style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid var(--bg)', cursor: 'pointer', fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onClick={() => handleSelect(product)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div>
                        <strong>{product.name || product.nazwa}</strong>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          EAN: {product.barcode || product.ean || '-'} | Kod: {product.product_code || product.kod_produktu || '-'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                          backgroundColor: (product.stock_quantity || 0) > 0 ? 'var(--success)' : 'var(--text-muted)',
                          color: '#fff'
                        }}>
                          {product.stock_quantity || 0} szt.
                        </span>
                        <i className="fas fa-plus-circle" style={{ color: 'var(--success)', fontSize: '14px' }}></i>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Selected products list */}
          <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <span style={{ fontWeight: '600', fontSize: '11px' }}>
                <i className="fas fa-list-check me-1" style={{ color: 'var(--purple)' }}></i>
                Wybrane produkty ({selectedProducts.length})
              </span>
              {selectedProducts.length > 0 && (
                <button
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', color: 'var(--danger)' , cursor: 'pointer' }}
                  onClick={clearSelectedProducts}
                >
                  <i className="fas fa-trash me-1"></i>Wyczysc
                </button>
              )}
            </div>

            {selectedProducts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '11px' }}>
                <i className="fas fa-info-circle me-1"></i>
                Wyszukaj i dodaj produkty do inwentaryzacji czesciowej
              </div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--border)' }}>
                    <tr>
                      {['LP', 'Produkt', 'Kod', 'Stan', ''].map(h => (
                        <th key={h} style={{ padding: '0.25rem 0.5rem', textAlign: h === '' || h === 'LP' ? 'center' : 'left', fontWeight: '600', width: h === 'LP' ? '35px' : 'auto' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProducts.map((product, index) => (
                      <tr key={product.id} style={{ backgroundColor: 'var(--card-bg)', borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)' }}>{index + 1}</td>
                        <td style={{ padding: '0.25rem 0.5rem' }}><strong>{product.name || product.nazwa}</strong></td>
                        <td style={{ padding: '0.25rem 0.5rem' }}>
                          <code style={{ fontSize: '10px', backgroundColor: 'var(--border)', padding: '1px 4px', borderRadius: '2px' }}>{product.barcode || product.product_code || 'Brak'}</code>
                        </td>
                        <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>
                          <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: 'var(--cyan)', color: '#fff' , borderRadius: '3px' }}>{product.system_count || 0}</span>
                        </td>
                        <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>
                          <button
                            style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', color: 'var(--danger)' , cursor: 'pointer' }}
                            onClick={() => removeFromSelectedProducts(product.id)}
                            title="Usun z listy"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

export default NewInventoryPanel;
